import React, { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { builderLabelStyle, builderHelpStyle } from '@/components/creation-flow/BuilderCard';

export interface TaxonomyCategory { slug: string; label: string; }

// Shared category + tags fields for the three product builders. Category is a
// single curated slug (subject, not format); tags are a small folksonomy with
// typeahead from tags already in use on the marketplace.
export default function CategoryTagsFields({
  category, setCategory, tags, setTags,
}: {
  category: string;
  setCategory: (c: string) => void;
  tags: string[];
  setTags: (t: string[]) => void;
}) {
  const [taxonomy, setTaxonomy] = useState<{ categories: TaxonomyCategory[]; tags: string[] }>({ categories: [], tags: [] });
  const [draft, setDraft] = useState('');
  const listId = useRef(`tags-dl-${Math.random().toString(36).slice(2, 8)}`).current;

  useEffect(() => {
    let cancelled = false;
    fetch('/api/marketplace/taxonomy').then(r => r.json())
      .then(d => { if (!cancelled && d?.categories) setTaxonomy({ categories: d.categories, tags: d.tags || [] }); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const addTag = (raw: string) => {
    const slug = raw.toLowerCase().trim().replace(/[^a-z0-9 -]/g, '').replace(/\s+/g, '-').slice(0, 30);
    if (!slug || tags.includes(slug) || tags.length >= 8) return;
    setTags([...tags, slug]);
    setDraft('');
  };

  return (
    <>
      <div>
        <Label style={builderLabelStyle}>Category</Label>
        <Select value={category || undefined} onValueChange={setCategory}>
          <SelectTrigger><SelectValue placeholder="Pick a category" /></SelectTrigger>
          <SelectContent>
            {taxonomy.categories.map(c => <SelectItem key={c.slug} value={c.slug}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <p style={builderHelpStyle}>How buyers browse — one subject per listing.</p>
      </div>
      <div>
        <Label style={builderLabelStyle}>Tags</Label>
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5" style={{ marginBottom: 8 }}>
            {tags.map(t => (
              <span key={t} className="inline-flex items-center gap-1" style={{ padding: '3px 8px', borderRadius: 999, background: 'var(--hj-beige-hover)', border: '1px solid var(--hj-beige-line)', fontSize: 12, color: 'var(--hj-beige-ink)' }}>
                {t}
                <button type="button" onClick={() => setTags(tags.filter(x => x !== t))} style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', color: 'var(--hj-beige-ink-3)', display: 'inline-flex' }} aria-label={`Remove ${t}`}>
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}
        <Input
          value={draft}
          list={listId}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addTag(draft); } }}
          onBlur={() => draft.trim() && addTag(draft)}
          placeholder={tags.length >= 8 ? 'Max 8 tags' : 'e.g. cold-reading — Enter to add'}
          disabled={tags.length >= 8}
        />
        <datalist id={listId}>
          {taxonomy.tags.filter(t => !tags.includes(t)).map(t => <option key={t} value={t} />)}
        </datalist>
        <p style={builderHelpStyle}>Up to 8 — help search and related listings find you.</p>
      </div>
    </>
  );
}
